import { useCallback, useEffect, useRef, useState } from 'react';
import API, { getCancelToken } from 'services/API';
import strings from 'strings';
import { Endpoints } from 'UI/constants/endpoints';
import { RequestStatus } from 'UI/constants/status';

const INITIAL_CATALOG_COLORS = {
  candidateTypeColors: {},
  candidateStatusColors: {}
};

const mapCandidateTypeColors = candidateTypes => {
  if (!Array.isArray(candidateTypes)) return {};

  return candidateTypes.reduce((acc, candidateType) => {
    if (candidateType?.id === null || candidateType?.id === undefined) {
      return acc;
    }

    return {
      ...acc,
      [candidateType.id]: candidateType.style_class_name || candidateType.color
    };
  }, {});
};

const mapCandidateStatusColors = candidateStatuses => {
  if (!Array.isArray(candidateStatuses)) return {};

  return candidateStatuses.reduce((acc, candidateStatus) => {
    if (candidateStatus?.id === null || candidateStatus?.id === undefined) {
      return acc;
    }

    return {
      ...acc,
      [candidateStatus.id]: candidateStatus.style || candidateStatus.color
    };
  }, {});
};

export const useResumeSearchCatalogs = () => {
  const catalogsRequestRef = useRef(null);
  const cachedCatalogColorsRef = useRef(null);
  const cancelTokenRef = useRef(null);
  const [uiState, setUiState] = useState(RequestStatus.Idle);
  const [catalogColors, setCatalogColors] = useState(INITIAL_CATALOG_COLORS);

  const getResumeSearchCatalogColors = useCallback(async () => {
    if (cachedCatalogColorsRef.current) {
      return cachedCatalogColorsRef.current;
    }

    if (!catalogsRequestRef.current) {
      cancelTokenRef.current = getCancelToken();

      catalogsRequestRef.current = Promise.all([
        API.get(Endpoints.CandidateTypes, {
          cancelToken: cancelTokenRef.current.token
        }),
        API.get(Endpoints.CandidateStatuses, {
          cancelToken: cancelTokenRef.current.token
        })
      ])
        .then(([candidateTypesResponse, candidateStatusesResponse]) => {
          const mappedColors = {
            candidateTypeColors: mapCandidateTypeColors(candidateTypesResponse.data),
            candidateStatusColors: mapCandidateStatusColors(candidateStatusesResponse.data)
          };

          cancelTokenRef.current = null;
          cachedCatalogColorsRef.current = mappedColors;
          return mappedColors;
        })
        .catch(error => {
          cancelTokenRef.current = null;
          catalogsRequestRef.current = null;
          throw error;
        });
    }

    return catalogsRequestRef.current;
  }, []);

  useEffect(() => {
    const fetchCatalogs = async () => {
      if (cachedCatalogColorsRef.current) {
        setCatalogColors(cachedCatalogColorsRef.current);
        setUiState(RequestStatus.Success);
        return;
      }

      setUiState(RequestStatus.Loading);

      try {
        const mappedColors = await getResumeSearchCatalogColors();

        setCatalogColors(mappedColors);
        setUiState(RequestStatus.Success);
      } catch (error) {
        if (error?.message === strings.shared.requests.cancelTokenMessage) {
          return;
        }

        setUiState(RequestStatus.Error);
      }
    };

    fetchCatalogs();

    return () => {
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel(strings.shared.requests.cancelTokenMessage);
        cancelTokenRef.current = null;
      }
    };
  }, [getResumeSearchCatalogColors]);

  return {
    ...catalogColors,
    uiState
  };
};
